import React, { useCallback } from 'react';
import Big from 'big.js';
import exchangeFormValidation from './validation';
import IExchangeForm, { ITradingSettings, IUseExchangeFormPayload, TExchangeFormState, TSupportedTradingCurrencies } from './IExchangeForm';
import { IWalletDetails } from '@market/interfaces/IWallet';

export const useExchangeForm = (userWallets: IWalletDetails[], urlCurrency: TSupportedTradingCurrencies, exchangeType: IExchangeForm['type']) => {
  const initialValue: TExchangeFormState = {
    values: {
      step: 1,
      outcomeAmount: null,
      outcomeCurrency: getOutcomeCurrency(exchangeType, urlCurrency, null),
      selectCurrency: null,
      exchangeRate: undefined,
      settings: undefined,
      exchangeRateChangeTime: getExchangeRateUpdateTime(),
    },
    errors: {
      initial: 'initial error disables Next button',
    },
  };

  const reducer = useCallback((
    state: TExchangeFormState,
    action: {
      type: string;
      payload: IUseExchangeFormPayload;
    }
  ) => {
    switch (action.type) {
      case 'edit':
        const additionalData = getAdditionalData(action.payload, urlCurrency, exchangeType);
        const editNewValues = {
          ...state.values,
          ...additionalData,
          [action.payload.name]: action.payload.value,
        };
        return {
          values: editNewValues,
          errors: exchangeFormValidation(editNewValues, action.payload, userWallets),
        };

      case 'setMaximumAmount':
        const balance = userWallets.find(el => el.currency === state.values.outcomeCurrency)?.balance;
        const maximumAmount = getMaximumAmount(
          state.values.settings?.current,
          balance
        );
        const setMaximumNewValues = { ...state.values, outcomeAmount: maximumAmount };
        return {
          ...state,
          values: setMaximumNewValues,
          errors: exchangeFormValidation(
            setMaximumNewValues,
            { name: 'setMaximumAmount', value: maximumAmount },
            userWallets
          ),
        };

      case 'updateExchangeRateDate':
        const additional = getAdditionalData(action.payload, urlCurrency, exchangeType);
        return {
          ...state,
          values: {
            ...state.values,
            ...additional,
            exchangeRateChangeTime: getExchangeRateUpdateTime(),
          },
        };

      case 'resetState':
        return initialValue;

      default:
        return state;
    }
  }, [userWallets, urlCurrency, exchangeType]);

  const [state, dispatch] = React.useReducer(reducer, initialValue);

  return {
    dispatch,
    values: state.values,
    errors: state.errors,
  };
};

const getAdditionalData = (
  payload: IUseExchangeFormPayload,
  urlCurrency: TSupportedTradingCurrencies,
  exchangeType: IExchangeForm['type']
): Partial<TExchangeFormState['values']> => {
  if (payload.name === 'selectCurrency') {
    const outcomeCurrency = getOutcomeCurrency(
      exchangeType,
      urlCurrency,
      payload.value as TExchangeFormState['values']['selectCurrency']
    );

    return { outcomeCurrency, exchangeRate: undefined };
  }

  return {};
};

const getMaximumAmount = (
  currentSettings: ITradingSettings | undefined | null,
  outcomeCurrencyWalletBallance: number | undefined
) => {
  if (currentSettings && outcomeCurrencyWalletBallance) {
    const maximumAmount = currentSettings.maximum_amount_per_trade;
    const minimumAmount = currentSettings.minimum_amount_per_trade;

    if (Big(outcomeCurrencyWalletBallance).lt(maximumAmount)) {
      if (Big(outcomeCurrencyWalletBallance).gte(minimumAmount)) {
        return outcomeCurrencyWalletBallance;
      }

      return outcomeCurrencyWalletBallance;
    }

    return maximumAmount;
  }

  return 0;
};

const getOutcomeCurrency = (
  exchangeType: IExchangeForm['type'],
  urlCurrency: TSupportedTradingCurrencies,
  selectCurrency: TExchangeFormState['values']['selectCurrency']
) => exchangeType === 'buy' ? selectCurrency : urlCurrency;

export const getExchangeRateUpdateTime = () => {
  const secondsMargin = 30;
  const date = new Date();

  date.setSeconds(date.getSeconds() + secondsMargin);
  return date.toISOString();
};
